import { useEffect, useRef, useState } from "react";
import { useExperience } from "../../../context/ExperienceContext";
import { experienceModeOptions } from "../../../config/mode-theme.config";
import "./ThemeSwitch.css";

export default function ThemeSwitch() {
  const { mode, setModeWithTransition } = useExperience();

  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);

  const togglePanel = () => setIsOpen((prev) => !prev);
  const closePanel = () => setIsOpen(false);

  const handleSelect = (value, event) => {
    const rect = event.currentTarget.getBoundingClientRect();

    setModeWithTransition(value, {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    });

    closePanel();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isOpen) return;

      if (panelRef.current && !panelRef.current.contains(event.target)) {
        closePanel();
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closePanel();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, {
      passive: true,
    });
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  return (
    <aside
      ref={panelRef}
      className={`theme-switch-floating ${isOpen ? "is-open" : ""}`}
      aria-label="Filtro de experiência"
    >
      <button
        type="button"
        className={`theme-switch-trigger ${isOpen ? "is-open" : ""}`}
        onClick={togglePanel}
        aria-label={
          isOpen ? "Fechar filtro de experiência" : "Abrir filtro de experiência"
        }
        aria-expanded={isOpen}
      >
        <span className="theme-switch-trigger__glow" />

        <span className="theme-switch-trigger__icon" aria-hidden="true">
          {!isOpen ? (
            <svg
              viewBox="0 0 24 24"
              className="theme-switch-trigger__svg theme-switch-trigger__svg--filter"
            >
              <path
                d="M4 7h16M7 12h10M10 17h4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <span className="theme-switch-trigger__x">
              <span />
              <span />
            </span>
          )}
        </span>

        <span className="theme-switch-trigger__label">Filtro</span>
      </button>

      <div className={`theme-switch-panel ${isOpen ? "is-open" : ""}`}>
        <div className="theme-switch-wrapper">
          <div className="theme-switch-header">
            <span className="theme-switch-title">Filtrar experiência</span>

            <button
              type="button"
              className="theme-switch-close"
              onClick={closePanel}
              aria-label="Fechar painel"
            >
              ×
            </button>
          </div>

          <div
            className="theme-switch"
            role="tablist"
            aria-label="Filtro de experiência"
          >
            {experienceModeOptions.map((option) => {
              const isActive = mode === option.id;

              return (
                <button
                  key={option.id}
                  type="button"
                  className={`theme-switch-button ${option.bodyClass} ${
                    isActive ? "active" : ""
                  }`}
                  style={{
                    "--mode-primary": option.primaryColor,
                    "--mode-primary-strong": option.primaryColorStrong,
                    "--mode-primary-soft": option.primaryColorSoft,
                    "--mode-active-gradient": option.activeGradient,
                    "--mode-active-shadow": option.activeShadow,
                  }}
                  onClick={(event) => handleSelect(option.id, event)}
                  aria-pressed={isActive}
                >
                  <span className="theme-switch-button-text">
                    {option.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </aside>
  );
}